import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Search, Users, Truck, Edit, Trash2, ShoppingCart } from "lucide-react";
import { customersAPI, suppliersAPI, getApiErrorPayload } from "@/lib/api";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { useToast } from "@/hooks/use-toast";
import { getBestBadgeVariant, cn } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { CustomerStoreCreditPanel } from "@/features/customers/components/CustomerStoreCreditPanel";
import { CustomerLoyaltyPanel } from "@/features/customers/components/CustomerLoyaltyPanel";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";

type ContactType = "customers" | "suppliers";

interface ContactRow {
	id: number;
	number: string;
	name: string;
	phone?: string;
	email?: string;
	status: string;
	totalSales?: number;
	totalSpent?: number;
}

/** Only the fields the backend actually persists (audited against the DTOs). */
interface ContactForm {
	name: string;
	phone: string;
	email: string;
	taxNumber: string; // suppliers only
	address: string;
	notes: string;
	isActive: boolean;
}

const emptyForm: ContactForm = {
	name: "",
	phone: "",
	email: "",
	taxNumber: "",
	address: "",
	notes: "",
	isActive: true,
};

/**
 * Customers and suppliers merged into one contacts page:
 * a type toggle over a single table, with add/edit as a dialog
 * limited to the fields the backend really saves.
 */
const ContactsPage: React.FC = () => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();
	const [searchParams, setSearchParams] = useSearchParams();

	const canCustomers = usePermission("customers.read");
	const canSuppliers = usePermission("suppliers.read");

	const initialType: ContactType =
		searchParams.get("type") === "suppliers" && canSuppliers
			? "suppliers"
			: canCustomers
				? "customers"
				: "suppliers";
	const [type, setType] = useState<ContactType>(initialType);

	const [rows, setRows] = useState<ContactRow[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedStatus, setSelectedStatus] = useState<"active" | "inactive" | "">("");

	// Header counters
	const [customersCount, setCustomersCount] = useState<number | null>(null);
	const [suppliersCount, setSuppliersCount] = useState<number | null>(null);

	// Add/edit dialog
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editingType, setEditingType] = useState<ContactType>("customers");
	const [editingRewardPoints, setEditingRewardPoints] = useState(0);
	const [form, setForm] = useState<ContactForm>(emptyForm);
	const [formLoading, setFormLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const mapCustomer = (c: any): ContactRow => ({
		id: c.id,
		number: c.customerNumber || "-",
		name: c.fullName || [c.firstName, c.lastName].filter(Boolean).join(" ") || "-",
		phone: c.phone,
		email: c.email,
		status: c.status || "active",
		totalSales: c.totalSales,
		totalSpent: c.totalSpent,
	});

	const mapSupplier = (s: any): ContactRow => ({
		id: s.id,
		number: s.supplierNumber || "-",
		name: s.name || "-",
		phone: s.phone,
		email: s.email,
		status: s.status || "active",
	});

	const fetchRows = useCallback(async () => {
		try {
			setIsLoading(true);
			if (type === "customers") {
				const res = await customersAPI.getAll({
					page: currentPage,
					limit: itemsPerPage,
					search: searchTerm || undefined,
					status: selectedStatus || undefined,
				});
				setRows((res.customers || []).map(mapCustomer));
				setTotalItems(res.total || 0);
				setTotalPages(Math.ceil((res.total || 0) / itemsPerPage));
			} else {
				const res = await suppliersAPI.getAll({
					page: currentPage,
					limit: itemsPerPage,
					search: searchTerm || undefined,
					status: selectedStatus || undefined,
				});
				setRows((res.suppliers || []).map(mapSupplier));
				setTotalItems(res.total || 0);
				setTotalPages(Math.ceil((res.total || 0) / itemsPerPage));
			}
		} catch (err) {
			toast({
				title: t("common.error"),
				description: t(type === "customers" ? "customers.errors.fetchFailed" : "suppliers.errors.fetchFailed"),
				variant: "destructive",
			});
			setRows([]);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on list inputs
	}, [type, currentPage, itemsPerPage, searchTerm, selectedStatus]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	const fetchCounts = useCallback(() => {
		if (canCustomers) {
			customersAPI
				.getAll({ page: 1, limit: 1 })
				.then((res: any) => setCustomersCount(res.total || 0))
				.catch(() => setCustomersCount(null));
		}
		if (canSuppliers) {
			suppliersAPI
				.getAll({ page: 1, limit: 1 })
				.then((res: any) => setSuppliersCount(res.total || 0))
				.catch(() => setSuppliersCount(null));
		}
	}, [canCustomers, canSuppliers]);

	useEffect(() => {
		fetchCounts();
	}, [fetchCounts]);

	const switchType = (next: ContactType) => {
		if (next === type) return;
		setType(next);
		setCurrentPage(1);
		setSearchTerm("");
		setSelectedStatus("");
	};

	const openCreate = () => {
		setEditingId(null);
		setEditingType(type);
		setForm(emptyForm);
		setDialogOpen(true);
	};

	const openEdit = useCallback(
		async (contactType: ContactType, id: number) => {
			setEditingId(id);
			setEditingType(contactType);
			setEditingRewardPoints(0);
			setForm(emptyForm);
			setDialogOpen(true);
			setFormLoading(true);
			try {
				if (contactType === "customers") {
					const c = await customersAPI.getById(id);
					setEditingRewardPoints(Number(c.rewardPoints) || 0);
					setForm({
						name: c.fullName || [c.firstName, c.lastName].filter(Boolean).join(" "),
						phone: c.phone || "",
						email: c.email || "",
						taxNumber: "",
						address: c.address || "",
						notes: c.notes || "",
						isActive: (c.status || "active") === "active",
					});
				} else {
					const s = await suppliersAPI.getById(id);
					setForm({
						name: s.name || "",
						phone: s.phone || "",
						email: s.email || "",
						taxNumber: s.taxNumber || "",
						address: s.address || "",
						notes: s.notes || "",
						isActive: (s.status || "active") === "active",
					});
				}
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t(
						contactType === "customers" ? "customers.errors.fetchFailed" : "suppliers.errors.fetchFailed",
					),
					variant: "destructive",
				});
				setDialogOpen(false);
			} finally {
				setFormLoading(false);
			}
		},
		[t, toast],
	);

	// Deep links kept from the old pages: ?edit=<id> and ?new=1
	useEffect(() => {
		const editId = searchParams.get("edit");
		const isNew = searchParams.get("new") === "1";
		const linkType: ContactType = searchParams.get("type") === "suppliers" ? "suppliers" : "customers";
		if (editId) {
			void openEdit(linkType, parseInt(editId, 10));
		} else if (isNew) {
			setEditingId(null);
			setEditingType(linkType);
			setForm(emptyForm);
			setDialogOpen(true);
		}
		if (editId || isNew) {
			setSearchParams(
				prev => {
					const next = new URLSearchParams(prev);
					next.delete("edit");
					next.delete("new");
					return next;
				},
				{ replace: true },
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once for deep links
	}, []);

	const handleSave = async () => {
		if (!form.name.trim()) {
			toast({
				title: t("common.error"),
				description: t(
					editingType === "customers" ? "customers.errors.fullNameRequired" : "suppliers.errors.nameRequired",
				),
				variant: "destructive",
			});
			return;
		}
		const status = form.isActive ? "active" : "inactive";
		try {
			setSaving(true);
			if (editingType === "customers") {
				const payload = {
					fullName: form.name.trim(),
					phone: form.phone.trim() || undefined,
					email: form.email.trim() || undefined,
					address: form.address.trim() || undefined,
					notes: form.notes.trim() || undefined,
					status,
				};
				if (editingId) await customersAPI.update(editingId, payload);
				else await customersAPI.create(payload);
				toast({
					title: t("common.success"),
					description: t(editingId ? "customers.messages.updated" : "customers.messages.created"),
				});
			} else {
				const payload = {
					name: form.name.trim(),
					phone: form.phone.trim() || undefined,
					email: form.email.trim() || undefined,
					taxNumber: form.taxNumber.trim() || undefined,
					address: form.address.trim() || undefined,
					notes: form.notes.trim() || undefined,
					status,
				};
				if (editingId) await suppliersAPI.update(editingId, payload);
				else await suppliersAPI.create(payload);
				toast({
					title: t("common.success"),
					description: t(editingId ? "suppliers.messages.updated" : "suppliers.messages.created"),
				});
			}
			setDialogOpen(false);
			fetchRows();
			fetchCounts();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage === payload.key ? t("common.errors.unexpected") : errorMessage,
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (row: ContactRow) => {
		const isCustomer = type === "customers";
		if (
			!(await confirmAction(t(isCustomer ? "customers.confirmations.delete" : "suppliers.confirmations.delete"), {
				confirmLabel: t("common.delete"),
			}))
		) {
			return;
		}
		try {
			if (isCustomer) await customersAPI.delete(row.id);
			else await suppliersAPI.delete(row.id);
			toast({
				title: t("common.success"),
				description: t(isCustomer ? "customers.messages.deleted" : "suppliers.messages.deleted"),
			});
			fetchRows();
			fetchCounts();
		} catch (err) {
			toast({
				title: t("common.error"),
				description: t(isCustomer ? "customers.messages.deleteFailed" : "suppliers.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	const getStatusBadge = (status: string) => (
		<Badge variant={getBestBadgeVariant(status === "active" ? "active" : "inactive")}>
			{status === "active" ? t("common.active") : t("common.inactive")}
		</Badge>
	);

	const isCustomers = type === "customers";
	const createPermission = isCustomers ? "customers.create" : "suppliers.create";
	const updatePermission = isCustomers
		? ["customers.read", "customers.update"]
		: ["suppliers.read", "suppliers.update"];
	const deletePermission = isCustomers ? "customers.delete" : "suppliers.delete";
	const canCreate = usePermission(createPermission);

	return (
		<div className="space-y-4 p-4 md:p-6">
			<PageHeader
				title={isCustomers ? t("customers.header.title") : t("suppliers.title")}
				description={isCustomers ? t("customers.header.description") : t("suppliers.description")}
				actions={
					<PermissionGate permission={createPermission}>
						<Button className="gap-2" onClick={openCreate}>
							<Plus className="h-4 w-4" />
							{isCustomers ? t("customers.header.addButton") : t("suppliers.actions.create")}
						</Button>
					</PermissionGate>
				}
			/>

			{/* Type toggle + search + status filter */}
			<div className="filter-bar flex flex-col gap-3 rounded-2xl border border-border/60 bg-card p-4 sm:flex-row sm:items-center">
				<div className="flex gap-2">
					{canCustomers && (
						<button
							type="button"
							onClick={() => switchType("customers")}
							className={cn(
								"flex items-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors",
								isCustomers
									? "bg-primary/10 text-primary border-transparent font-semibold"
									: "bg-card text-muted-foreground hover:border-primary/50",
							)}
						>
							<Users className="h-4 w-4" />
							{t("navigation.customers")}{" "}
							<span className="text-xs tabular-nums">{customersCount ?? "–"}</span>
						</button>
					)}
					{canSuppliers && (
						<button
							type="button"
							onClick={() => switchType("suppliers")}
							className={cn(
								"flex items-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors",
								!isCustomers
									? "bg-primary/10 text-primary border-transparent font-semibold"
									: "bg-card text-muted-foreground hover:border-primary/50",
							)}
						>
							<Truck className="h-4 w-4" />
							{t("navigation.suppliers")}{" "}
							<span className="text-xs tabular-nums">{suppliersCount ?? "–"}</span>
						</button>
					)}
				</div>
				<div className="relative flex-1 sm:max-w-sm">
					<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("common.search")}
						value={searchTerm}
						onChange={e => {
							setSearchTerm(e.target.value);
							setCurrentPage(1);
						}}
						className="ps-10"
					/>
				</div>
				<select
					aria-label={t("common.status")}
					value={selectedStatus}
					onChange={e => {
						setSelectedStatus(e.target.value as "active" | "inactive" | "");
						setCurrentPage(1);
					}}
					className="flex h-12 w-full sm:w-44 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					<option value="">{t("customers.filters.allStatuses")}</option>
					<option value="active">{t("common.active")}</option>
					<option value="inactive">{t("common.inactive")}</option>
				</select>
			</div>

			{/* Contacts table */}
			<section>
				<div>
					{isLoading ? (
						<Loading />
					) : rows.length === 0 ? (
						searchTerm.trim() || selectedStatus ? (
							<EmptyState icon={Search} title={t("common.noResults")} />
						) : (
							<EmptyState
								icon={isCustomers ? Users : Truck}
								title={isCustomers ? t("customers.empty.title") : t("suppliers.empty.title")}
								action={
									canCreate
										? {
												label: isCustomers
													? t("customers.header.addButton")
													: t("suppliers.actions.create"),
												onClick: openCreate,
											}
										: undefined
								}
							/>
						)
					) : (
						<div className="my-1">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>#</TableHead>
										<TableHead>
											{isCustomers
												? t("customers.table.headers.customerNumber")
												: t("suppliers.table.headers.supplierNumber")}
										</TableHead>
										<TableHead>{t("customers.table.headers.name")}</TableHead>
										<TableHead className="text-center">
											{t("customers.table.headers.phone")}
										</TableHead>
										<TableHead>{t("customers.table.headers.email")}</TableHead>
										{isCustomers && (
											<>
												<TableHead className="text-center">
													{t("customers.table.headers.totalSales")}
												</TableHead>
												<TableHead className="text-center">
													{t("customers.table.headers.totalSpent")}
												</TableHead>
											</>
										)}
										<TableHead className="text-center">
											{t("customers.table.headers.status")}
										</TableHead>
										<TableHead className="text-center w-28">{t("common.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map(row => (
										<TableRow key={row.id}>
											<TableCell className="whitespace-nowrap tabular-nums">{row.id}</TableCell>
											<TableCell className="whitespace-nowrap font-medium">
												<bdi>{row.number}</bdi>
											</TableCell>
											<TableCell>{row.name}</TableCell>
											<TableCell className="text-center" dir="ltr">
												{row.phone || "-"}
											</TableCell>
											<TableCell className="text-sm">
												<span className="inline-block max-w-64 break-words [overflow-wrap:anywhere]">
													{row.email || "-"}
												</span>
											</TableCell>
											{isCustomers && (
												<>
													<TableCell className="text-center">
														<div className="flex justify-center items-center gap-2">
															<ShoppingCart className="h-4 w-4 text-muted-foreground" />
															{row.totalSales || 0}
														</div>
													</TableCell>
													<TableCell className="text-center font-medium">
														<Price value={row.totalSpent || 0} currency={currency} />
													</TableCell>
												</>
											)}
											<TableCell className="text-center">{getStatusBadge(row.status)}</TableCell>
											<TableCell className="text-center">
												<div className="flex items-center justify-center gap-2">
													<PermissionGate permission={updatePermission}>
														<Button
															variant="outline"
															size="sm"
															onClick={() => openEdit(type, row.id)}
															title={t("common.edit")}
															aria-label={t("common.edit")}
														>
															<Edit className="h-4 w-4 text-primary" />
														</Button>
													</PermissionGate>
													<PermissionGate permission={deletePermission}>
														<Button
															variant="outline"
															size="sm"
															onClick={() => handleDelete(row)}
															title={t("common.delete")}
															aria-label={t("common.delete")}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</PermissionGate>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</div>
			</section>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalItems}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName={isCustomers ? "customers.pagination.customers" : "suppliers.pagination.suppliers"}
			/>

			{/* Add / Edit dialog (only the fields the backend persists) */}
			<Dialog open={dialogOpen} onOpenChange={open => !open && setDialogOpen(false)}>
				<DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingType === "customers"
								? editingId
									? t("customers.actions.edit")
									: t("customers.actions.create")
								: editingId
									? t("suppliers.actions.edit")
									: t("suppliers.actions.create")}
						</DialogTitle>
						<DialogDescription>
							{editingType === "customers"
								? editingId
									? t("customers.form.editDescription")
									: t("customers.form.createDescription")
								: editingId
									? t("suppliers.form.editDescription")
									: t("suppliers.form.createDescription")}
						</DialogDescription>
					</DialogHeader>
					{formLoading ? (
						<Loading />
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="contact-name">
									{editingType === "customers"
										? t("customers.form.fullName")
										: t("suppliers.form.name")}{" "}
									*
								</Label>
								<Input
									id="contact-name"
									value={form.name}
									onChange={e => setForm({ ...form, name: e.target.value })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="contact-phone">{t("customers.form.phone")}</Label>
								<Input
									id="contact-phone"
									dir="ltr"
									value={form.phone}
									onChange={e => setForm({ ...form, phone: e.target.value })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="contact-email">{t("customers.form.email")}</Label>
								<Input
									id="contact-email"
									type="email"
									dir="ltr"
									value={form.email}
									onChange={e => setForm({ ...form, email: e.target.value })}
								/>
							</div>
							{editingType === "suppliers" && (
								<div className="space-y-2">
									<Label htmlFor="contact-taxNumber">{t("suppliers.form.taxNumber")}</Label>
									<Input
										id="contact-taxNumber"
										dir="ltr"
										value={form.taxNumber}
										onChange={e => setForm({ ...form, taxNumber: e.target.value })}
									/>
								</div>
							)}
							<div className="space-y-2">
								<Label htmlFor="contact-address">{t("customers.form.address")}</Label>
								<Input
									id="contact-address"
									value={form.address}
									onChange={e => setForm({ ...form, address: e.target.value })}
								/>
							</div>
							<div className="md:col-span-2 space-y-2">
								<Label htmlFor="contact-notes">{t("customers.form.notes")}</Label>
								<Textarea
									id="contact-notes"
									rows={2}
									value={form.notes}
									onChange={e => setForm({ ...form, notes: e.target.value })}
								/>
							</div>
							<div className="flex items-center justify-between rounded-lg border p-3 md:col-span-2">
								<Label htmlFor="contact-active" className="text-sm font-medium">
									{t("common.active")}
								</Label>
								<Switch
									id="contact-active"
									checked={form.isActive}
									onCheckedChange={checked => setForm({ ...form, isActive: checked })}
								/>
							</div>

							{/* Store credit & loyalty live here now (moved from the old customer page) */}
							{editingType === "customers" && editingId != null && (
								<div className="md:col-span-2 space-y-4">
									<CustomerStoreCreditPanel customerId={editingId} />
									<CustomerLoyaltyPanel customerId={editingId} rewardPoints={editingRewardPoints} />
								</div>
							)}
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleSave} disabled={saving || formLoading}>
							{saving ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							) : (
								<span>{t("common.save")}</span>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default ContactsPage;
